import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  api,
  ApiError,
  companyDisplayName,
  configCompanies,
  type ClientConfig,
  type ConfigCompany,
} from "../api.js";
import { useToken } from "../auth.js";
import { sealBase64 } from "../crypto.js";

// Screen where the operator chooses which Sage companies this machine may query and
// types their passwords, which are sealed against the machine public key in this
// browser and then POSTed to the cloud as ciphertext. After a successful submit the
// plaintext is cleared from the component state — nothing goes back to the input field.
//
// Ticking a company is what grants this machine access to that database: the agent only
// offers companies whose credentials it actually holds. Unticking a previously sealed
// company revokes it.

interface CompanyEntry {
  checked: boolean;
  password: string;
  dbPassword: string;
}

// Which credential fields a company's own config actually calls for. A SQL-only company
// has no Web API password to ask about, and showing an unused field invites the operator
// to fill it with something meaningless.
function needsSqlPassword(c: ConfigCompany): boolean {
  return Boolean(c.sqlServer && c.database && c.dbUser);
}
function needsWebApiPassword(c: ConfigCompany): boolean {
  return Boolean(c.baseUrl && c.username && c.tenant && c.company);
}

export function SealSecrets() {
  const { id: customerId, machineId } = useParams<{ id: string; machineId: string }>();
  const token = useToken();
  const { t } = useLingui();

  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [sealedKeys, setSealedKeys] = useState<Set<string>>(new Set());
  const [entries, setEntries] = useState<Record<string, CompanyEntry>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  // Source of truth for the list is the config payload, never the sealed keys: a
  // company that leaves the payload should disappear from this screen even if a
  // ciphertext for its slug lingers in the DB.
  //
  // Disabled companies stay visible so an operator can see the machine's residual
  // sealed access to them, but their checkbox is disabled — sealing a disabled
  // company is meaningless.
  const companies = configCompanies(config ?? undefined);

  const refresh = useCallback(async () => {
    if (!customerId || !machineId) return;
    setLoading(true);
    setError(null);
    try {
      const [pk, cfg] = await Promise.all([
        api.getMachinePublicKey(token, machineId),
        api.getConfig(token, customerId),
      ]);
      setPublicKey(pk.publicKey);
      setFingerprint(pk.fingerprint);
      setConfig(cfg.config);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [customerId, machineId, token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Sealed keys are indexed by machine (the config is now single-row per
  // customer). Re-runs on refresh so a fresh save of the config also updates
  // which checkboxes are pre-checked.
  const loadSealedKeys = useCallback(async () => {
    if (!customerId || !machineId || !config) return;
    try {
      const res = await api.listSealedSecretKeys(token, customerId, { machineId });
      setSealedKeys(new Set(res.keys.map((k) => k.key)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [customerId, machineId, token, config]);

  useEffect(() => {
    void loadSealedKeys();
  }, [loadSealedKeys]);

  // Pre-check whatever this machine already holds, so the screen shows the machine's
  // real access rather than an empty form every time.
  useEffect(() => {
    setEntries((prev) => {
      const next: Record<string, CompanyEntry> = {};
      for (const c of companies) {
        const already =
          sealedKeys.has(`${c.id}.password`) || sealedKeys.has(`${c.id}.dbPassword`);
        // Keep whatever the operator has already typed or ticked in this session;
        // only a row we have never seen takes its state from the server.
        next[c.id] = prev[c.id] ?? {
          checked: already,
          password: "",
          dbPassword: "",
        };
      }
      return next;
    });
    // `companies` is derived from config; depending on it directly keeps this
    // effect from re-firing on every render.
  }, [config, sealedKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSealed = (id: string) =>
    sealedKeys.has(`${id}.password`) || sealedKeys.has(`${id}.dbPassword`);

  const patch = (id: string, changes: Partial<CompanyEntry>) =>
    setEntries((prev) => ({ ...prev, [id]: { ...prev[id], ...changes } }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!customerId || !machineId || !publicKey || !config) return;

    // Disabled companies cannot be checked in the UI, but guard on submit too:
    // a race between reactivate-elsewhere and this in-flight seal shouldn't send
    // credentials for a row that just went cold.
    const checked = companies.filter(
      (c) => entries[c.id]?.checked && !c.disabled,
    );
    const revoked = companies.filter((c) => !entries[c.id]?.checked && isSealed(c.id));

    // A checked company with empty fields means "already sealed, leave it alone" — we
    // must send no key for it rather than an empty ciphertext.
    const toSeal = checked.filter(
      (c) => entries[c.id].password.length > 0 || entries[c.id].dbPassword.length > 0,
    );
    // A newly checked company with nothing typed would be granted access it cannot use.
    const incomplete = checked.filter(
      (c) =>
        !isSealed(c.id) &&
        entries[c.id].password.length === 0 &&
        entries[c.id].dbPassword.length === 0,
    );
    if (incomplete.length > 0) {
      setError(
        t`Enter a password for: ${incomplete.map(companyDisplayName).join(", ")}`,
      );
      return;
    }
    if (toSeal.length === 0 && revoked.length === 0) {
      setError(t`Nothing to change.`);
      return;
    }
    if (revoked.length > 0) {
      const names = revoked.map(companyDisplayName).join(", ");
      if (!window.confirm(t`This machine will lose access to: ${names}. Continue?`)) {
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      let stored = 0;
      if (toSeal.length > 0) {
        const secrets: Array<{ key: string; ciphertextB64: string }> = [];
        for (const c of toSeal) {
          const entry = entries[c.id];
          if (entry.password) {
            secrets.push({
              key: `${c.id}.password`,
              ciphertextB64: await sealBase64(publicKey, entry.password),
            });
          }
          if (entry.dbPassword) {
            secrets.push({
              key: `${c.id}.dbPassword`,
              ciphertextB64: await sealBase64(publicKey, entry.dbPassword),
            });
          }
        }
        const res = await api.uploadEncryptedSecrets(token, customerId, {
          machineId,
          secrets,
        });
        stored = res.stored;
      }

      let deleted = 0;
      if (revoked.length > 0) {
        const keys = revoked.flatMap((c) =>
          [`${c.id}.password`, `${c.id}.dbPassword`].filter((k) => sealedKeys.has(k)),
        );
        const res = await api.deleteEncryptedSecrets(token, customerId, {
          machineId,
          keys,
        });
        deleted = res.deleted;
      }

      setSuccess(
        t`Sealed ${stored} secret(s) and revoked ${deleted}. The machine will pick up the change over the WebSocket push — no restart needed.`,
      );
      // Wipe plaintext from component state after a successful seal, then refresh
      // the key list so the checkboxes reflect what the machine actually holds now.
      setEntries((prev) => {
        const next: Record<string, CompanyEntry> = {};
        for (const [id, entry] of Object.entries(prev)) {
          next[id] = { ...entry, password: "", dbPassword: "" };
        }
        return next;
      });
      await loadSealedKeys();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p className="muted"><Trans>Loading…</Trans></p>;

  return (
    <div className="detail">
      <header className="page-header">
        <div>
          <h1><Trans>Seal secrets</Trans></h1>
          <p className="muted">
            <Link to={`/customers/${customerId}`}><Trans>← Back to customer</Trans></Link>
          </p>
        </div>
      </header>

      {error && <div className="banner err">{error}</div>}
      {success && <div className="banner ok">{success}</div>}

      <section className="key-info">
        <div>
          <span className="label"><Trans>Machine public key fingerprint</Trans></span>
          <code className="fp">{fingerprint ?? "—"}</code>
        </div>
        <p className="muted small">
          <Trans>
            Passwords typed below are sealed in this browser with the machine's public key
            and never sent in the clear. After sealing, the plaintext is discarded from
            the form.
          </Trans>
        </p>
      </section>

      <form onSubmit={submit} className="config-form">
        <fieldset>
          <legend><Trans>Companies this machine may query</Trans></legend>
          <p className="muted small">
            <Trans>
              Tick a company to give this machine access to its database. Leave a
              password blank on an already-sealed company to keep the current one.
            </Trans>
          </p>

          {companies.length === 0 && (
            <p className="muted">
              <Trans>The remote config declares no company yet.</Trans>
            </p>
          )}

          {companies.map((c) => {
            const entry = entries[c.id] ?? { checked: false, password: "", dbPassword: "" };
            return (
              <div key={c.id} className="company-row" data-disabled={c.disabled ? "true" : undefined}>
                <label className="inline">
                  <input
                    type="checkbox"
                    checked={entry.checked && !c.disabled}
                    onChange={(e) => patch(c.id, { checked: e.target.checked })}
                    // Sealing a disabled company would only grant access to a
                    // row that refuses new turns anyway. Reactivate it first.
                    disabled={c.disabled}
                  />{" "}
                  <strong>{companyDisplayName(c)}</strong> <code>{c.id}</code>
                  {c.disabled && (
                    <span className="badge warn"> <Trans>disabled</Trans></span>
                  )}
                  {isSealed(c.id) && (
                    <span className="badge"> <Trans>already sealed</Trans></span>
                  )}
                </label>
                <p className="muted small">
                  {c.sqlServer && c.database ? `${c.sqlServer}/${c.database}` : "—"}
                  {c.tenant && c.company ? ` · ${c.tenant}/${c.company}` : ""}
                </p>

                {entry.checked && needsWebApiPassword(c) && (
                  <label>
                    <Trans>Sage 300 Web API password</Trans>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={entry.password}
                      onChange={(e) => patch(c.id, { password: e.target.value })}
                    />
                  </label>
                )}
                {entry.checked && needsSqlPassword(c) && (
                  <label>
                    <Trans>SQL Server password</Trans>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={entry.dbPassword}
                      onChange={(e) => patch(c.id, { dbPassword: e.target.value })}
                    />
                  </label>
                )}
                {entry.checked && !needsSqlPassword(c) && !needsWebApiPassword(c) && (
                  <p className="banner err">
                    <Trans>
                      This company has neither complete SQL nor complete Web API settings,
                      so there is no password to seal. Fix the config first.
                    </Trans>
                  </p>
                )}
              </div>
            );
          })}
        </fieldset>

        <div className="actions">
          <Link to={`/customers/${customerId}`}>
            <button type="button" className="ghost">
              <Trans>Done</Trans>
            </button>
          </Link>
          <button
            type="submit"
            disabled={submitting || !publicKey || !config || companies.length === 0}
          >
            {submitting ? <Trans>Sealing…</Trans> : <Trans>Seal & upload</Trans>}
          </button>
        </div>
      </form>
    </div>
  );
}
